import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import apiHandler from "../../js/apiHandler";
import showdown from "showdown";
import "../../styles/hostAgentChat.less";

const HOST_AGENT_POLL_INTERVAL_MS = 2000;
// Fail if we receive no progress update within this window (backend retries reset it).
const HOST_AGENT_POLL_TIMEOUT_MS = 60 * 1000;
const HOST_AGENT_ERROR_MESSAGE =
	"Something went wrong. Please try again later.";

const resolveStatusPath = (statusCheckUrl) => {
	if (!statusCheckUrl || typeof statusCheckUrl !== "string") return "";
	if (statusCheckUrl.startsWith("/")) return statusCheckUrl;
	if (statusCheckUrl.includes("papi-v1")) {
		return statusCheckUrl.substring(
			statusCheckUrl.indexOf("papi-v1") + "papi-v1".length,
		);
	}
	try {
		const u = new URL(statusCheckUrl);
		return `${u.pathname}${u.search || ""}`;
	} catch {
		return statusCheckUrl;
	}
};

const getGuideLabel = (guideOp) => {
	if (!guideOp) return "Walkthrough";
	return guideOp.title || guideOp.description || guideOp.id || "Walkthrough";
};

const getGuideUrlPatterns = (guideOp) => {
	const context = guideOp?.start_context || {};
	return Array.isArray(context.url_patterns) ? context.url_patterns : [];
};

const normalizeGuidePattern = (pattern) => String(pattern || "").replace(/\\\//g, "/");

const guideHasReservationPattern = (guideOp) => getGuideUrlPatterns(guideOp).some((pattern) => {
	const source = normalizeGuidePattern(pattern);
	return (
		source.includes("/reservations/") ||
		source.includes("/dashboard/reservations/") ||
		source.includes("/properties/reservations/")
	);
});

const guideHasPropertyPattern = (guideOp) => getGuideUrlPatterns(guideOp).some((pattern) => {
	const source = normalizeGuidePattern(pattern);
	return (
		!source.includes("/properties/reservations") &&
		(source.includes("/properties/") || source.includes("/collections/"))
	);
});

const guideHasReservationIntent = (guideOp) => {
	const text = [
		guideOp?.title,
		guideOp?.description,
		...(Array.isArray(guideOp?.tags) ? guideOp.tags : []),
	].filter(Boolean).join(" ").toLowerCase();

	return /\breservation\b|\bguest\b|\binvoice\b|\bpayment\b/.test(text);
};

const getGuidePriority = (guideOp) => {
	if (guideHasReservationPattern(guideOp)) return 30;
	if (guideHasReservationIntent(guideOp)) return 20;
	if (guideHasPropertyPattern(guideOp)) return 10;
	return 0;
};

const getGuideTitleKey = (guideOp) => String(getGuideLabel(guideOp)).trim().toLowerCase();

const normalizeGuideOptions = (parsed) => {
	const options = Array.isArray(parsed?.guide_options) ? parsed.guide_options : [];
	const fallback = parsed?.guide_op ? [parsed.guide_op] : [];
	const seen = new Set();
	const guides = [];

	[...options, ...fallback].filter(Boolean).forEach((guideOp) => {
		const key = guideOp.id || JSON.stringify(guideOp.start_context || {}) + getGuideLabel(guideOp);
		if (seen.has(key)) return;
		seen.add(key);

		const titleKey = getGuideTitleKey(guideOp);
		const existingIndex = guides.findIndex((existing) => (
			getGuideTitleKey(existing) === titleKey &&
			(guideHasReservationIntent(existing) || guideHasReservationIntent(guideOp))
		));

		if (existingIndex >= 0) {
			if (getGuidePriority(guideOp) > getGuidePriority(guides[existingIndex])) {
				guides[existingIndex] = guideOp;
			}
			return;
		}

		guides.push(guideOp);
	});

	return guides;
};

const getAuditLogPath = (auditLogPath) => {
	if (!auditLogPath) return "";
	if (typeof auditLogPath === "string") return auditLogPath;
	return auditLogPath.audit_log_path || auditLogPath.path || "";
};

const getAuditLogButtonHtml = (auditLogPath) => {
	const path = getAuditLogPath(auditLogPath);
	if (!path) return "";
	const url = `/reports/audit-logs?audit_log_path=${encodeURIComponent(path)}`;
	return `<a class="host-agent-chat-audit-btn" href="${url}" target="_blank" rel="noopener noreferrer">View Audit Log</a>`;
};

const parseStatusResponse = (response) => {
	if (response == null) return null;
	if (typeof response === "object") return response;
	if (typeof response !== "string") return null;
	const trimmed = response.trim();
	if (!trimmed) return null;
	try {
		return JSON.parse(trimmed);
	} catch {
		return null;
	}
};

const isProgressOnlyResponse = (payload) => {
	if (!payload || typeof payload !== "object") return false;
	const hasProgress =
		Boolean(payload.progress) || Object.prototype.hasOwnProperty.call(payload, "partial_message");
	const hasMessage = Boolean(String(payload.message || "").trim());
	const hasGuides =
		Boolean(payload.guide_op) ||
		(Array.isArray(payload.guide_options) && payload.guide_options.length > 0);
	return hasProgress && !hasMessage && !hasGuides;
};

const isEmptyAgentResponse = (response) => {
	if (response == null) return true;
	const parsed = parseStatusResponse(response);
	if (parsed && typeof parsed === "object") {
		if (isProgressOnlyResponse(parsed)) return true;
		const message = parsed.message;
		if (message != null) {
			return (
				!String(message).trim() &&
				!parsed.guide_op &&
				!(Array.isArray(parsed.guide_options) && parsed.guide_options.length)
			);
		}
		return Object.keys(parsed).length === 0;
	}
	if (typeof response === "string") return !response.trim();
	if (typeof response === "object") return Object.keys(response).length === 0;
	return false;
};

const attachAuditLogLinksToMessage = (message, auditLogPaths) => {
	const buttons = (Array.isArray(auditLogPaths) ? auditLogPaths : [])
		.map(getAuditLogButtonHtml)
		.filter(Boolean);
	if (!buttons.length) return message;

	let buttonIdx = 0;
	const lines = String(message || "").split(/\r?\n/);
	const linkedLines = lines.map((line) => {
		if (buttonIdx >= buttons.length) return line;
		if (/^\s*(?:[-*+]|\d+\.)\s+/.test(line)) {
			const nextLine = `${line} ${buttons[buttonIdx]}`;
			buttonIdx += 1;
			return nextLine;
		}
		return line;
	});

	if (buttonIdx < buttons.length) {
		return `${linkedLines.join("\n")}\n\n${buttons.slice(buttonIdx).join("\n")}`;
	}
	return linkedLines.join("\n");
};

function HostAgentChat({ onClose, embedded, onStartGuide }) {
	const orgName = useSelector((state) => state.roleManager.org);

	const [messages, setMessages] = useState(() => {
		const saved = sessionStorage.getItem("host_agent_chat_messages");
		return saved ? JSON.parse(saved) : [];
	});
	const [userInput, setUserInput] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);
	const [progressMessage, setProgressMessage] = useState(null);
	const [partialMessage, setPartialMessage] = useState(null);

	const converter = useRef(new showdown.Converter());
	const messagesEndRef = useRef(null);
	const pollTimerRef = useRef(null);

	const toggleSnapEngage = (show) => {
		const el =
			document.getElementById("designstudio-button") ||
			document.querySelector('[id^="snap-engage"], [class*="snapengage"]');
		if (el) el.style.display = show ? "" : "none";
	};

	const scrollToBottom = () => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
		}
	};

	// Mount / unmount
	useEffect(() => {
		toggleSnapEngage(false);
		return () => {
			if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
			const hasMessages = sessionStorage.getItem("host_agent_chat_messages");
			if (!hasMessages) {
				toggleSnapEngage(true);
			}
		};
	}, []);

	// Persist messages to sessionStorage and scroll to bottom on every change
	useEffect(() => {
		sessionStorage.setItem("host_agent_chat_messages", JSON.stringify(messages));
		scrollToBottom();
	}, [messages]);

	useEffect(() => {
		if (progressMessage || partialMessage) scrollToBottom();
	}, [progressMessage, partialMessage]);

	const getReservationId = () => {
		const path = window.location.pathname || "";
		const patterns = [
			/\/dashboard\/reservations\/(\d+)/,
			/\/properties\/reservations\/(\d+)/,
			/\/reservations\/(\d+)/,
		];
		for (const pattern of patterns) {
			const match = path.match(pattern);
			if (match) return match[1];
		}
		return null;
	};

	const getPropertyIdFromPath = () => {
		const path = window.location.pathname || "";
		// /properties/reservations/:id is a reservation route, not a property route.
		if (path.includes("/properties/reservations/")) return null;
		const match = path.match(/\/properties\/(\d+)/);
		return match ? match[1] : null;
	};

	const getPropertyIdFromPage = () => {
		const anchors = document.querySelectorAll('a[href*="/properties/"]');
		for (const anchor of anchors) {
			const href = anchor.getAttribute("href") || "";
			if (href.includes("/properties/reservations/")) continue;
			const match = href.match(/\/properties\/(\d+)/);
			if (match) return match[1];
		}
		return null;
	};

	const resolvePropertyId = async (reservationId) => {
		const fromPath = getPropertyIdFromPath();
		if (fromPath) return fromPath;

		const fromPage = getPropertyIdFromPage();
		if (fromPage) return fromPage;

		if (!reservationId) return null;
		try {
			const response = await apiHandler.get({
				apiName: "rapapi",
				path: `/properties/reservations/${reservationId}`,
			});
			return response?.data?.property_id || null;
		} catch (err) {
			console.warn("Unable to resolve property_id for host agent context", err);
			return null;
		}
	};

	const getCurrentPage = () => `${window.location.pathname}${window.location.search || ""}`;

	const pollStatus = (statusCheckUrl) => {
		const statusPath = resolveStatusPath(statusCheckUrl);
		let startedAt = Date.now();
		let lastProgressText = null;
		let lastPartialText = null;

		return new Promise((resolve, reject) => {
			const clearPollTimer = () => {
				if (pollTimerRef.current) {
					clearTimeout(pollTimerRef.current);
					pollTimerRef.current = null;
				}
			};

			const check = async () => {
				if (Date.now() - startedAt >= HOST_AGENT_POLL_TIMEOUT_MS) {
					clearPollTimer();
					reject(new Error(HOST_AGENT_ERROR_MESSAGE));
					return;
				}

				try {
					const response = await apiHandler.get({
						apiName: "rapapi",
						path: statusPath,
					});
					const data = response?.data;
					if (data?.status === "error") {
						clearPollTimer();
						reject(new Error(data.error || HOST_AGENT_ERROR_MESSAGE));
						return;
					}
					if (data?.status === "completed") {
						clearPollTimer();
						if (isEmptyAgentResponse(data.response)) {
							reject(new Error(HOST_AGENT_ERROR_MESSAGE));
							return;
						}
						resolve(data.response);
						return;
					}
					const responsePayload = parseStatusResponse(data?.response);
					const progress = responsePayload?.progress;
					const partial = responsePayload?.partial_message || "";
					let sawUpdate = false;
					if (progress && progress !== lastProgressText) {
						lastProgressText = progress;
						setProgressMessage(progress);
						sawUpdate = true;
					}
					if (partial && partial !== lastPartialText) {
						lastPartialText = partial;
						setPartialMessage(partial);
						sawUpdate = true;
					}
					if (sawUpdate) {
						startedAt = Date.now();
					}
					pollTimerRef.current = setTimeout(check, HOST_AGENT_POLL_INTERVAL_MS);
				} catch (err) {
					clearPollTimer();
					reject(err);
				}
			};
			pollTimerRef.current = setTimeout(check, HOST_AGENT_POLL_INTERVAL_MS);
		});
	};

	const sendMessage = async () => {
		if (!userInput.trim() || isLoading) return;

		const newMessages = [
			...messages,
			{ role: "host", content: userInput.trim() },
		];
		setMessages(newMessages);
		setUserInput("");
		setIsLoading(true);
		setErrorMessage(null);
		setProgressMessage(null);
		setPartialMessage(null);

		const isReportsChangesPage = window.location.pathname.includes(
			"/reports/audit-logs",
		);

		try {
			const reservationId = getReservationId();
			const propertyId = await resolvePropertyId(reservationId);
			const actionData = {
				organization_name: orgName,
				property_id: propertyId,
				current_page: getCurrentPage(),
				reservation_id: reservationId,
				messages: newMessages.map((m) => ({
					role: m.role,
					content: m.content,
				})),
			};

			if (isReportsChangesPage) {
				actionData.host_agent_mode = "audit_only";
			}

			const response = await apiHandler.post({
				apiName: "cbapi",
				path: "/agents/host_agent/actions",
				options: {
					body: {
						action_type: "cbapi_agent_chat",
						action_data: actionData,
					},
				},
			});

			const statusCheckUrl = response?.data?.status_check_url;
			if (!statusCheckUrl) throw new Error("No status check URL returned");

			const result = await pollStatus(statusCheckUrl);
			let agentMessage = "";
			let guideOp = null;
			let guideOptions = [];

			const parsed = parseStatusResponse(result);
			if (parsed && typeof parsed === "object") {
				agentMessage = parsed.message || "";
				guideOptions = normalizeGuideOptions(parsed);
				guideOp = parsed.guide_op || guideOptions[0] || null;

				if (parsed.audit_log_paths && Array.isArray(parsed.audit_log_paths)) {
					agentMessage = attachAuditLogLinksToMessage(
						agentMessage,
						parsed.audit_log_paths,
					);
				}
			} else if (typeof result === "string") {
				agentMessage = result;
			}

			const guideLabel = guideOp ? getGuideLabel(guideOp) : null;

			if (!String(agentMessage || "").trim() && !guideOptions.length) {
				throw new Error(HOST_AGENT_ERROR_MESSAGE);
			}

			setMessages([
				...newMessages,
				{ role: "agent", content: agentMessage, guideOp, guideOptions, guideLabel },
			]);
			setIsLoading(false);
			setProgressMessage(null);
			setPartialMessage(null);
		} catch (err) {
			setIsLoading(false);
			setProgressMessage(null);
			setPartialMessage(null);
			setErrorMessage(err.message || HOST_AGENT_ERROR_MESSAGE);
		}
	};

	const startNewConversation = () => {
		sessionStorage.removeItem("host_agent_chat_messages");
		setMessages([]);
		setUserInput("");
		setErrorMessage(null);
	};

	const handleStartGuide = (guideOp) => {
		if (!guideOp || !onStartGuide) return;
		onStartGuide(guideOp);
	};

	const onKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			sendMessage();
		}
	};

	const messagesArea = (
		<div className="host-agent-chat-messages">
			{messages.length === 0 && !isLoading && (
				<div className="host-agent-chat-empty">
					Ask a question to get started.
				</div>
			)}
			{messages.map((msg, idx) => {
				const isHost = msg.role === "host";
				const htmlContent = converter.current.makeHtml(msg.content);
				const guideOptions = Array.isArray(msg.guideOptions) && msg.guideOptions.length
					? msg.guideOptions
					: msg.guideOp
						? [msg.guideOp]
						: [];
				const guideLabel = guideOptions.length === 1
					? msg.guideLabel || msg.guideTitle || getGuideLabel(guideOptions[0])
					: null;
				return (
					<div
						key={idx}
						className={`host-agent-chat-bubble ${isHost ? "host-bubble" : "agent-bubble"}`}>
						<div className="host-agent-chat-role">
							{isHost ? "You" : "Agent"}
						</div>
						<div
							className="host-agent-chat-content"
							dangerouslySetInnerHTML={{ __html: htmlContent }}
						/>
						{guideOptions.length > 1 && (
							<div className="host-agent-chat-guide-options">
								{guideOptions.map((option, optionIdx) => (
									<details
										key={option.id || optionIdx}
										className="host-agent-chat-guide-option"
										open={optionIdx === 0}>
										<summary className="host-agent-chat-guide-option-summary">
											<span>{getGuideLabel(option)}</span>
										</summary>
										{option.description && (
											<div className="host-agent-chat-guide-option-description">
												{option.description}
											</div>
										)}
										<button
											className="host-agent-chat-guide-btn"
											onClick={() => handleStartGuide(option)}>
											Show Me
										</button>
									</details>
								))}
							</div>
						)}
						{guideLabel && guideOptions.length === 1 && (
							<button
								className="host-agent-chat-guide-btn"
								onClick={() => handleStartGuide(guideOptions[0])}>
								<svg
									width="14"
									height="14"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									style={{ verticalAlign: "-2px", marginRight: "6px" }}>
									<circle cx="12" cy="12" r="10" />
									<path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
									<line x1="12" y1="17" x2="12.01" y2="17" />
								</svg>
								Show Me: {guideLabel}
							</button>
						)}
					</div>
				);
			})}
			{isLoading && (
				<div className="host-agent-chat-bubble agent-bubble host-agent-chat-bubble-pending">
					<div className="host-agent-chat-role">Agent</div>
					{progressMessage ? (
						<div className="host-agent-chat-progress">{progressMessage}</div>
					) : (
						<div className="host-agent-chat-typing">
							<span className="dot" />
							<span className="dot" />
							<span className="dot" />
						</div>
					)}
					{partialMessage ? (
						<div className="host-agent-chat-partial">{partialMessage}</div>
					) : null}
				</div>
			)}
			{errorMessage && (
				<div className="host-agent-chat-error">{errorMessage}</div>
			)}
			<div ref={messagesEndRef} />
		</div>
	);

	const inputArea = (
		<div className="host-agent-chat-input-area">
			<textarea
				className="host-agent-chat-textarea"
				placeholder="Type your question..."
				value={userInput}
				onChange={(e) => setUserInput(e.target.value)}
				onKeyDown={onKeyDown}
				disabled={isLoading}
				rows={2}
			/>
			<button
				className="host-agent-chat-btn-send"
				onClick={sendMessage}
				disabled={isLoading || !userInput.trim()}
				title="Send">
				<i className="icon-send" />
				Send
			</button>
		</div>
	);

	if (embedded) {
		return (
			<div className="host-agent-chat-embedded">
				{messages.length > 0 && (
					<div className="host-agent-chat-embedded-toolbar">
						<button
							className="host-agent-chat-btn-new-embedded"
							onClick={startNewConversation}
							disabled={isLoading}>
							<i className="icon-Plus" /> New conversation
						</button>
					</div>
				)}
				{messagesArea}
				{inputArea}
			</div>
		);
	}

	return (
		<div className="host-agent-chat-panel">
			<div className="host-agent-chat-header">
				<span className="host-agent-chat-title">Chat</span>
				<div className="host-agent-chat-header-actions">
					<button
						className="host-agent-chat-btn-new"
						onClick={startNewConversation}
						disabled={isLoading || messages.length === 0}
						title="New Conversation">
						<i className="icon-Plus" /> New
					</button>
					<button
						className="host-agent-chat-btn-close"
						onClick={onClose}
						title="Minimize">
						<svg
							width="18"
							height="18"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2.5"
							strokeLinecap="round">
							<line x1="5" y1="12" x2="19" y2="12" />
						</svg>
					</button>
				</div>
			</div>
			{messagesArea}
			{inputArea}
		</div>
	);
}

export default HostAgentChat;
